import React, { useState } from 'react';
import { MessageSquareIcon } from 'lucide-react';
import type { TranscriptComment } from '../utils/transcriptComments';
import { formatEditStamp } from '../utils/time';
interface GeneralCommentsProps {
  /** Comments about the transcript as a whole, plus any whose passage has gone. */
  comments: TranscriptComment[];
  canComment: boolean;
  onAdd: (body: string) => Promise<boolean>;
}

/**
 * General transcript comments.
 *
 * A quiet control near the top rather than a permanent column: notes that are
 * not about a particular passage open in a compact panel and close again, so
 * the transcript stays the dominant thing on the page.
 */
export function GeneralComments({
  comments,
  canComment,
  onAdd
}: GeneralCommentsProps) {
  const [open, setOpen] = useState(false);
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [failed, setFailed] = useState('');
  async function submit(event: React.FormEvent) {
    event.preventDefault();
    if (!body.trim() || sending) return;
    setSending(true);
    setFailed('');
    const ok = await onAdd(body.trim());
    setSending(false);
    if (!ok) {
      setFailed('Not saved — try again.');
      return;
    }
    setBody('');
  }
  if (!canComment && comments.length === 0) return null;
  return <div className="relative mt-8 max-w-4xl" data-id="element-426" data-mp="vt6xsx3z5lyc">
      <button type="button" onClick={() => setOpen(!open)} aria-expanded={open} className="label flex items-center gap-3 text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-427" data-mp="vs5clzt8vj2w">
        <MessageSquareIcon className="h-3.5 w-3.5" strokeWidth={1.25} data-id="element-428" data-mp="84fekv8d4dxp" />
        {canComment ? '+ Add general comment' : 'General comments'}
        {comments.length > 0 && <span className="text-graphite" data-id="element-429" data-mp="d3ryb73xt4w2">· {comments.length}</span>}
      </button>

      {open && <div className="absolute left-0 top-9 z-30 w-[26rem] max-w-[90vw] border border-ink bg-paper p-5 shadow-sm" data-id="element-430" data-mp="3v9wta2fkw7y9">
          {comments.length > 0 && <ul className="m-0 mb-5 list-none p-0" data-id="element-431" data-mp="25s9ep0p5c2id">
              {comments.map(comment => <li key={comment.id} className="border-b border-rule py-4 first:pt-0" data-id="element-432" data-mp="210zqm4j8x78l">
                  {comment.quote && <p className="m-0 border-l-2 border-rule/60 pl-3 font-display text-base leading-snug text-mute" data-id="element-433" data-mp="1w2y5le29exuh">
                      “{comment.quote}”
                    </p>}
                  <p className="mt-2 font-display text-lg font-normal leading-relaxed text-charcoal" data-id="element-434" data-mp="2oqbx2wn6v177">
                    {comment.body}
                  </p>
                  <span className="label-sm mt-2 block text-mute" data-id="element-435" data-mp="2es4uxjxk6np9">
                    {comment.authorName} · {formatEditStamp(comment.at)}
                    {comment.quote ? ' · passage since changed' : ''}
                  </span>
                </li>)}
            </ul>}

          {canComment ? <form onSubmit={submit} data-id="element-436" data-mp="zauq0sswyk7e">
              <label className="block" data-id="element-437" data-mp="ze0dy4pt3g1j">
                <span className="sr-only" data-id="element-438" data-mp="3azspb6pm6kxh">General comment</span>
                <textarea value={body} onChange={event => setBody(event.target.value)} rows={3} placeholder="About the whole transcript…" className="w-full resize-none border border-rule bg-paperDeep px-3 py-2 font-display text-lg leading-snug text-charcoal placeholder:text-mute focus:border-ink focus:outline-none" data-id="element-439" data-mp="3axg1we137id1" />
              </label>
              <div className="mt-3 flex flex-wrap items-center gap-4" data-id="element-440" data-mp="2iegl8wfy2lq">
                <button type="submit" disabled={!body.trim() || sending} className="label border border-ink px-4 py-2 transition-colors duration-150 ease-out hover:bg-ink hover:text-paper disabled:border-rule disabled:text-mute disabled:hover:bg-transparent disabled:hover:text-mute" data-id="element-441" data-mp="6lau4l74j8it">
                  {sending ? 'Adding…' : 'Add'}
                </button>
                <button type="button" onClick={() => setOpen(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-442" data-mp="1i0atqh36lclp">
                  Close
                </button>
                {failed && <p role="alert" className="label-sm m-0 text-ink" data-id="element-443" data-mp="1n1qcu3lst91g">
                    {failed}
                  </p>}
              </div>
            </form> : <button type="button" onClick={() => setOpen(false)} className="label text-mute transition-colors duration-150 ease-out hover:text-ink" data-id="element-444" data-mp="ucsvq3tbpiaa">
              Close
            </button>}
        </div>}
    </div>;
}